/**
 * Scotland Yard surface palette as CSS values.
 *
 * Each token uses the `light-dark()` CSS function so the colors swap
 * automatically with the global Mantine color scheme. The dark-mode hexes
 * preserve SY's original moody blue-slate palette exactly; the light-mode
 * picks are Mantine's neutral gray surfaces, scaled to keep the same
 * relative hierarchy (page < panel < surface < accent).
 *
 * Domain colors (player chips, transport routes, ticket types) are
 * intentionally NOT in here — those have semantic meaning and are not
 * theme-dependent. See `PLAYER_COLOR_HEX` / `TRANSPORT_COLOR` instead.
 */

export const SY = {
  /** Outermost page background — body of host TV / phone screens. */
  pageBg: 'light-dark(var(--mantine-color-gray-0), #0B0F1A)',
  /** Sidebar / scrubber panels — slightly lighter than page. */
  panelBg: 'light-dark(var(--mantine-color-gray-1), #0F172A)',
  /** Inset cards (player cards, ticket bars). */
  surfaceBg: 'light-dark(var(--mantine-color-white), #111827)',
  /** Backdrop behind the phone-frame mockup; deepest neutral. */
  mapBg: 'light-dark(var(--mantine-color-gray-2), #020617)',
  /** Hairline borders on cards and panel separators. */
  border: 'light-dark(var(--mantine-color-gray-3), #1F2937)',
  /** Soft / muted borders for nested rows. */
  borderSoft: 'light-dark(rgba(173, 181, 189, 0.4), rgba(31, 41, 55, 0.6))',
  /** Background for inline error / move-rejected messages. */
  dangerBg: 'light-dark(var(--mantine-color-red-1), #7F1D1D)',
  /** Border for the same. */
  dangerBorder: 'light-dark(var(--mantine-color-red-4), #B91C1C)',
  /** Highlight tint for reveal-round trail rows. Bumped in light mode. */
  revealHighlightBg: 'light-dark(rgba(250, 204, 21, 0.20), rgba(250, 204, 21, 0.08))',
  /** Border tint for reveal-round trail rows. Bumped in light mode. */
  revealHighlightBorder: 'light-dark(rgba(250, 204, 21, 0.5), rgba(250, 204, 21, 0.3))',
  /** Inner row background for the post-game path list. */
  revealRowBg: 'light-dark(rgba(241, 243, 245, 0.6), rgba(15, 23, 42, 0.6))',
} as const;

/** Always-on yellow accent for Mr. X branding. Same in both modes. */
export const SY_ACCENT_YELLOW = '#FACC15';
/** Always-on green for detective-win states. Same in both modes. */
export const SY_WIN_GREEN = '#22C55E';
